import { useEffect, useMemo, useState } from 'react'
import type { AxiosError } from 'axios'
import type { LabourRole } from '@entities/calculator'
import {
  MAX_INPUT_VALUE,
  parseInput,
  toDecimalString,
  useLabourRatesQuery,
  useSaveLabourRatesMutation,
} from '@entities/calculator'

interface RatesTabProps {
  roles: readonly LabourRole[]
  isLoading: boolean
}

const saveErrorText = (error: AxiosError | null): string | null => {
  if (!error) return null
  const status = error.response?.status
  if (status === 400)
    return 'Одну из ролей убрали из справочника — обновите страницу'
  if (status === 403)
    return 'Раздел доступен по подписке — похоже, она закончилась'
  if (status === 422) return 'Ставка не может быть отрицательной'
  return 'Не удалось сохранить ставки. Попробуйте ещё раз'
}

/**
 * «Ставки» — сколько стоит час работы каждой роли. Заводятся один раз и идут
 * во все рецепты: в самом рецепте указываются только часы.
 *
 * Форма отправляется целиком: ручка заменяет весь набор, поэтому копить
 * разницу бессмысленно — пустое поле и означает «ставки нет».
 */
const RatesTab = ({ roles, isLoading }: RatesTabProps) => {
  const ratesQuery = useLabourRatesQuery()
  const save = useSaveLabourRatesMutation()

  const saved = useMemo(() => {
    const map = new Map<number, string>()
    ;(ratesQuery.data?.rates ?? []).forEach(rate =>
      map.set(rate.role_id, rate.rate)
    )
    return map
  }, [ratesQuery.data])

  const [draft, setDraft] = useState<Record<number, string>>({})

  // Пришедшие ставки кладём в форму один раз на загрузку: затирать то, что
  // человек уже набрал, ответом сервера нельзя.
  useEffect(() => {
    if (!ratesQuery.data) return
    setDraft(Object.fromEntries(saved))
  }, [ratesQuery.data, saved])

  const isDirty = roles.some(
    role => (draft[role.role_id] ?? '') !== (saved.get(role.role_id) ?? '')
  )

  const invalid = roles.some(role => {
    const raw = draft[role.role_id]
    if (!raw) return false
    const value = parseInput(raw)
    return value === null || value < 0 || value > MAX_INPUT_VALUE
  })

  const handleSave = () => {
    const rates = roles
      .map(role => ({ role_id: role.role_id, raw: draft[role.role_id] ?? '' }))
      .filter(item => item.raw !== '')
      .map(item => ({
        role_id: item.role_id,
        rate: toDecimalString(parseInput(item.raw) ?? 0, 2),
      }))
    save.mutate(rates)
  }

  return (
    <div className="cost-tab">
      <p className="cost-tab__lead">
        Сколько стоит час работы. Ставку задают один раз — в рецепте останется
        указать только часы. Пустое поле значит, что роль не используется.
      </p>

      {(isLoading || ratesQuery.isLoading) && (
        <p className="cost-note">Загружаем ставки…</p>
      )}

      {!isLoading && roles.length === 0 && (
        <p className="cost-note">Ролей в справочнике пока нет.</p>
      )}

      <div className="cost-rates">
        {roles.map(role => (
          <label key={role.role_id} className="cost-rate">
            <span className="cost-rate__name">{role.name}</span>
            <span className="cost-rate__field">
              <input
                type="text"
                inputMode="decimal"
                value={draft[role.role_id] ?? ''}
                onChange={event =>
                  setDraft(current => ({
                    ...current,
                    [role.role_id]: event.target.value,
                  }))
                }
                placeholder="0"
                aria-label={`Ставка: ${role.name}`}
              />
              <span className="cost-rate__unit">₽/час</span>
            </span>
          </label>
        ))}
      </div>

      {save.isError && (
        <p className="cost-note warning">{saveErrorText(save.error ?? null)}</p>
      )}

      {!save.isError && save.isSuccess && !isDirty && (
        <p className="cost-note">Ставки сохранены.</p>
      )}

      <button
        type="button"
        className="cost-button wide"
        onClick={handleSave}
        disabled={!isDirty || invalid || save.isLoading}
      >
        {save.isLoading ? 'Сохраняем…' : 'Сохранить ставки'}
      </button>
    </div>
  )
}

export default RatesTab
